import { useCallback, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import { Empty, Space, Spin, Tag, Typography } from 'antd';

import BallooningViewer from '../../components/BallooningViewer';
import { Balloon } from '../../interfaces/Ballooning';
import {
	KeyenceValidationFinding,
	KeyenceValidationReport,
	KeyenceValidationSeverity,
} from '../../interfaces/Quality.Interface';
import { useGetBallooningProjectQuery } from '../../redux/api/ballooning';
import { SEVERITY_COLOR } from './constants';

const { Text } = Typography;

const STROKE_BY_SEVERITY: Record<KeyenceValidationSeverity, string> = {
	Error: '#ff4d4f',
	Warning: '#faad14',
	Info: '#1890ff',
};

interface Props {
	report: KeyenceValidationReport;
	findings: KeyenceValidationFinding[];
	activeBalloonId: string | null;
	setActiveBalloonId: (id: string | null) => void;
	centerOnActive: boolean;
	setCenterOnActive: (value: boolean) => void;
	pageNumber: number;
	setPageNumber: Dispatch<SetStateAction<number>>;
}

/** Worst severity wins when a balloon has multiple findings. */
const severityRank: Record<KeyenceValidationSeverity, number> = {
	Error: 3,
	Warning: 2,
	Info: 1,
};

export const resolveBalloonId = (
	finding: Pick<
		KeyenceValidationFinding,
		'balloon' | 'balloon_number' | 'suggested_balloon_number'
	>,
	balloonsByNumber: Map<number, Balloon>
): string | null => {
	if (finding.balloon) return finding.balloon;
	if (finding.balloon_number !== null) {
		return balloonsByNumber.get(finding.balloon_number)?.id ?? null;
	}
	if (finding.suggested_balloon_number !== null) {
		return balloonsByNumber.get(finding.suggested_balloon_number)?.id ?? null;
	}
	return null;
};

const ValidationDrawingPanel = ({
	report,
	findings,
	activeBalloonId,
	setActiveBalloonId,
	centerOnActive,
	setCenterOnActive,
	pageNumber,
	setPageNumber,
}: Props) => {
	const projectId = report.ballooning_project_id ?? '';
	const { data: projectData, isLoading } = useGetBallooningProjectQuery(
		{ id: projectId },
		{ skip: !projectId }
	);

	const viewBalloons = useMemo(() => {
		if (!projectData?.ballooning_pages) return [];
		const all = projectData.ballooning_pages.flatMap(
			(page) => page.balloons ?? []
		);
		if (!report.view) {
			return all.filter((balloon) => balloon.measurement_strategy === 'Keyence');
		}
		return all.filter(
			(balloon) =>
				balloon.measurement_strategy === 'Keyence' &&
				balloon.view === report.view
		);
	}, [projectData, report.view]);

	const balloonsByNumber = useMemo(() => {
		const map = new Map<number, Balloon>();
		viewBalloons.forEach((balloon) => map.set(balloon.balloon_number, balloon));
		return map;
	}, [viewBalloons]);

	const severityByBalloonId = useMemo(() => {
		const map = new Map<string, KeyenceValidationSeverity>();
		findings
			.filter((finding) => !finding.is_false_positive)
			.forEach((finding) => {
				const balloonId = resolveBalloonId(finding, balloonsByNumber);
				if (!balloonId) return;
				const existing = map.get(balloonId);
				if (
					!existing ||
					severityRank[finding.severity] > severityRank[existing]
				) {
					map.set(balloonId, finding.severity);
				}
			});
		return map;
	}, [findings, balloonsByNumber]);

	const issueCount = severityByBalloonId.size;

	const currentPageID = useMemo(() => {
		const page = projectData?.ballooning_pages?.find(
			(entry) => entry.page_number === pageNumber
		);
		return page?.id ?? '';
	}, [projectData, pageNumber]);

	useEffect(() => {
		if (viewBalloons.length === 0) return;
		const pageHasBalloons = viewBalloons.some(
			(balloon) => balloon.ballooning_page_number === pageNumber
		);
		if (pageHasBalloons) return;
		const firstPage = viewBalloons.reduce(
			(min, balloon) => Math.min(min, balloon.ballooning_page_number),
			viewBalloons[0].ballooning_page_number
		);
		setPageNumber(firstPage);
	}, [viewBalloons, pageNumber, setPageNumber]);

	useEffect(() => {
		if (!activeBalloonId) return;
		const balloon = viewBalloons.find((entry) => entry.id === activeBalloonId);
		if (balloon && balloon.ballooning_page_number !== pageNumber) {
			setPageNumber(balloon.ballooning_page_number);
		}
	}, [activeBalloonId, viewBalloons, pageNumber, setPageNumber]);

	const getBalloonStrokeColor = useCallback(
		(balloon: Balloon) => {
			const severity = severityByBalloonId.get(balloon.id);
			if (severity) return STROKE_BY_SEVERITY[severity];
			return undefined;
		},
		[severityByBalloonId]
	);

	if (!projectId) {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description="No inspection plan is linked to this report"
			/>
		);
	}

	if (isLoading || !projectData) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spin size="large" />
			</div>
		);
	}

	if (viewBalloons.length === 0) {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					report.view
						? `No Keyence balloons found for view "${report.view}"`
						: 'No Keyence balloons found in the inspection plan'
				}
			/>
		);
	}

	return (
		<div className="flex h-full flex-col">
			<Space size={8} wrap className="mb-2 px-1">
				{report.view && <Tag color="blue">View: {report.view}</Tag>}
				{issueCount > 0 ? (
					<>
						<Text type="secondary">{issueCount} balloon(s) with issues</Text>
						{(['Error', 'Warning'] as const).map((severity) =>
							[...severityByBalloonId.values()].includes(severity) ? (
								<Tag key={severity} color={SEVERITY_COLOR[severity]}>
									{severity}
								</Tag>
							) : null
						)}
					</>
				) : (
					<Text type="secondary">All balloons match — click a row below to locate it</Text>
				)}
			</Space>
			<div className="min-h-0 flex-1">
				{currentPageID ? (
					<BallooningViewer
						activeBalloonId={activeBalloonId}
						setActiveBalloonId={setActiveBalloonId}
						setCenterOnActive={setCenterOnActive}
						centerOnActive={centerOnActive}
						localBalloons={viewBalloons}
						setLocalBalloons={() => {}}
						balloonSize={projectData.balloon_size || 50}
						editable={false}
						pageID={currentPageID}
						pageNumber={pageNumber}
						setPageNumber={setPageNumber}
						isTabletView={false}
						projectId={projectId}
						fitMode="auto"
						showLeftPageBar={false}
						getBalloonStrokeColor={getBalloonStrokeColor}
					/>
				) : (
					<div className="flex h-full items-center justify-center">
						<Spin size="large" />
					</div>
				)}
			</div>
		</div>
	);
};

export default ValidationDrawingPanel;
